import { useState } from "react";
import PageHero from "../components/PageHero";

const FAQS = [
  {
    q: "What exactly does Nomad Livings do?",
    a: "Nomad Livings designs, builds and manages premium modular A-frame villas for hospitality investment. We offer five villa configurations — from cozy 1-bedroom units to luxury duplexes — each engineered for staycations, Airbnb rentals, boutique resorts and holiday homes. Investors own the villa while we handle construction, resort operations and bookings.",
  },
  {
    q: "Do I need to manage the villa or find guests myself?",
    a: "No. Our professional resort management team handles everything — marketing, bookings on Airbnb and OTAs, guest services, housekeeping and maintenance. You simply collect your rental returns and enjoy personal stays whenever you choose.",
  },
  {
    q: "Who is this investment best suited for?",
    a: "HNI and NRI investors seeking passive income, corporate professionals looking for a lifestyle asset, business owners diversifying into hospitality, families wanting a holiday home that earns, and anyone seeking 28-40% annual ROI from a tangible real estate asset.",
  },
  {
    q: "What are the five villa types available?",
    a: "We offer five modular A-frame configurations: Type 1 (1 Bedroom Villa), Type 2 (Duplex Villa), Type 3 (Four Cluster Suites), Type 4 (Dual Independent Units) and Type 5 (Premium Duplex Villa). Each is designed for different guest segments and investment budgets, starting from ₹29.9 Lakhs.",
  },
  {
    q: "How long does construction and delivery take?",
    a: "Thanks to our modular factory-fabricated system, construction is up to 60% faster than traditional builds. Most villas are delivered and resort-ready within 4-6 months from reservation, depending on site conditions and customization.",
  },
  {
    q: "Is my investment secure and legally protected?",
    a: "Yes. Every villa comes with clear title verification, proper approvals and permits, and a legally structured ownership model. Our in-house legal team handles compliance end-to-end so your asset is protected from day one.",
  },
  {
    q: "What revenue models can I choose from?",
    a: "We offer two primary models: Profit Sharing — where your returns are linked to the resort's overall performance with higher earning potential; and Minimum Rental — a fixed guaranteed annual income for investors who prefer stability and predictable cash flow.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="w-full">
      <script type="application/ld+json">
        {JSON.stringify(FAQ_SCHEMA)}
      </script>
      <PageHero
        eyebrow="FAQ"
        title={
          <>
            Frequently Asked <span className="italic text-accent-300">Questions</span>
          </>
        }
        subtitle="Everything you need to know about investing in a Nomad Livings A-frame villa."
      />

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-3xl mx-auto">
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = openIndex === i;
              return (
                <div
                  key={f.q}
                  className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(open ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="font-heading text-base md:text-lg text-foreground-950">{f.q}</span>
                    <span
                      className={`flex items-center justify-center w-8 h-8 shrink-0 rounded-full transition-colors ${
                        open ? "bg-primary-600 text-background-50" : "bg-background-50 text-foreground-800 border border-background-300"
                      }`}
                    >
                      <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
                    </span>
                  </button>
                  {open && (
                    <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-foreground-600">Still have questions?</p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 mt-3 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3 hover:bg-primary-700 transition-colors"
            >
              Contact Our Team <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}